import { memo, useId } from "react";
import type { LinkPreviewRefs } from "./useLinkGesture";

/**
 * The line a link is drawn with, and the note under the cursor saying what a
 * release would make.
 *
 * Always mounted and hidden until a gesture draws into it: the gesture writes
 * the path, colour and label directly (see `useLinkGesture`), so it must find
 * these elements already in the DOM on its first frame — and nothing here may
 * re-render while the pointer moves, which is why it takes only the refs and
 * the chart's size.
 */
export const GanttLinkPreview = memo(function GanttLinkPreview({
  preview,
  width,
  height,
}: {
  preview: LinkPreviewRefs;
  width: number;
  height: number;
}) {
  const markerId = `gantt-link-head-${useId().replace(/:/g, "")}`;
  return (
    <>
      <svg
        ref={preview.svg}
        className="pointer-events-none absolute left-0 top-0 overflow-visible"
        width={width}
        height={height}
        style={{ display: "none" }}
        aria-hidden="true"
        data-gantt-link-preview
      >
        <defs>
          {/* Filled with currentColor, which the gesture sets on the svg, so
              the head greys out with the line over a bar that refuses it.
              Marker units scale with the stroke: 6 here is a 12px head. */}
          <marker
            id={markerId}
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M0,0 L8,4 L0,8 z" fill="currentColor" />
          </marker>
        </defs>
        <path
          ref={preview.path}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          markerEnd={`url(#${markerId})`}
        />
      </svg>
      <div
        ref={preview.label}
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 z-40 whitespace-nowrap rounded border bg-background px-1.5 py-0.5 text-[11px] font-medium shadow-sm"
        style={{ display: "none" }}
      />
    </>
  );
});
